import { apiGet, apiPut, apiPost } from './client';
import type { SiteConfig, Page } from '@/types';

/**
 * Loads the tenant's site config AND merges in the list of pages.
 *
 * The gateway's `GET /site-config` returns the `site_configs.config` JSON blob,
 * which for many tenants does NOT include a `pages` array (pages live in the
 * `site_pages` table). The structured-pages rebuild only happens on the
 * slug-based `GET /site-config/:slug` route used by tenant-sites. The builder
 * needs both, so we fetch `/pages` in parallel and graft them onto the config.
 *
 * If `/pages` fails for any reason we degrade to an empty array — the store's
 * `setSiteConfig` also defaults `pages ?? []` defensively, so the editor still
 * loads instead of white-screening on a missing field.
 */
export async function getSiteConfig(): Promise<SiteConfig> {
  const [config, pagesResp] = await Promise.all([
    apiGet<SiteConfig>('/site-config'),
    apiGet<{ pages: Page[] }>('/pages').catch(() => ({ pages: [] as Page[] })),
  ]);
  // Normalize each page so consumers that iterate page.components (Canvas,
  // ComponentSidebar, etc.) don't trip on the /pages endpoint omitting that
  // field — `components: Component[]` is the type's contract but the API
  // surface only carries it when the legacy Proty path is active. Empty array
  // is the safe default for the block-model path D4a is verifying.
  const pages = (pagesResp?.pages ?? []).map((p) => ({
    ...p,
    components: p.components ?? [],
  }));
  // Navigation comes straight from GET /site-config, which reads the canonical
  // `site_navigation` table (site-scoped) and returns FLAT items carrying `id` +
  // `parent_id` — exactly what MenuEditor needs to rebuild dropdowns. Do NOT
  // re-fetch GET /navigation and remap to {label,url,page_id}: that dropped
  // id/parent_id (and nested children), so nested menus collapsed to a flat
  // top-level list on every reload (the "nesting doesn't persist" bug).
  return { ...config, pages };
}

export async function putSiteConfig(config: SiteConfig): Promise<SiteConfig> {
  return apiPut<SiteConfig>('/site-config', config);
}

export async function updateSiteConfig(
  updater: (current: SiteConfig) => SiteConfig | Promise<SiteConfig>,
): Promise<SiteConfig> {
  const current = await getSiteConfig();
  const next = await updater(current);
  return putSiteConfig(next);
}

export async function publishSiteConfig(): Promise<{ published_at: string; url: string }> {
  return apiPost<{ published_at: string; url: string }>('/site-config/publish');
}

/**
 * Persist the hero media for the home look's hero. Targeted partial write — the gateway merges
 * it into `site_configs.config.hero_media`, so unrelated config (pages, brand, nav) is untouched.
 * The SAME write the Hero Media panel performs; the tenant home render and the Studio preview
 * both prefer this over `style.hero_image_url` when painting the hero background.
 */
export async function setHeroMedia(heroMedia: NonNullable<SiteConfig['hero_media']>): Promise<void> {
  await apiPut('/site-config', { hero_media: heroMedia });
}

/**
 * Persist the per-section "Pick a Look" selection (home/search/listing/card/dark). Targeted
 * partial write — the gateway merges it into `site_configs.config.section_looks`, so unrelated
 * config (pages, brand, nav) is untouched. Same write the Pick-a-Look page uses; scoped to the
 * active site via the X-Site-Id header the client interceptor attaches.
 */
export async function setSectionLooks(looks: Record<string, string>): Promise<void> {
  await apiPut('/site-config', { section_looks: looks });
}

/**
 * Fetch a "Pick a Look" section preset's blocks (+ theme) — the SAME endpoint the live tenant
 * render uses (apps/tenant-sites fetchSectionLook). Lets the Studio preview apply a non-classic
 * HOME look exactly like the live site, so the editor preview matches what visitors see. Public,
 * no-auth endpoint; degrades to null blocks on any failure (→ Studio falls back to the site's own
 * blocks, its prior behavior).
 */
export interface SectionLookPreset {
  blocks: unknown[] | null;
  theme: Record<string, unknown> | null;
}
export async function fetchSectionLook(section: string, variantKey: string): Promise<SectionLookPreset> {
  if (!section || !variantKey) return { blocks: null, theme: null };
  try {
    const res = await apiGet<SectionLookPreset>(
      `/section-look?section=${encodeURIComponent(section)}&variant_key=${encodeURIComponent(variantKey)}`,
    );
    return {
      blocks: Array.isArray(res?.blocks) ? res.blocks : null,
      theme: res?.theme && typeof res.theme === 'object' ? res.theme : null,
    };
  } catch {
    return { blocks: null, theme: null };
  }
}
